import { Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { JsonService } from '../_services/json.service';
import { LoggerService } from '../_services/logger.service';
import { LoadingService } from '../_services/loading.service';
import { Country } from '../_models/country.model';
import { ZipContagemItem, ZipContagemResult } from '../_models/zip-contagem.model';

@Component({
  selector: 'app-zips-contagem',
  templateUrl: './zips-contagem.component.html',
  styleUrls: ['./zips-contagem.component.scss']
})
export class ZipsContagemComponent implements OnInit {
  readonly meses = [
    '01_ENERO', '02_FEBRERO', '03_MARZO', '04_ABRIL', '05_MAYO', '06_JUNIO',
    '07_JULIO', '08_AGOSTO', '09_SEPTIEMBRE', '10_OCTUBRE', '11_NOVIEMBRE', '12_DICIEMBRE'
  ];

  listaPaises: Country[] = [];
  filtro = {
    pais: '',
    ano: String(new Date().getFullYear()),
    mes: this.meses[new Date().getMonth()],
    incluirSubpastas: false
  };

  // Filtros aplicados na tela sobre o resultado já carregado (não vão ao SFTP).
  filtroLocal = { provider: '', envio: '', pdv: '' };

  resultado: ZipContagemResult | null = null;
  erro = '';

  currentPage = 1;
  pageSize = 15;

  constructor(
    private jsonService: JsonService,
    private logger: LoggerService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.jsonService.listCountry().subscribe({
      next: paises => this.listaPaises = paises,
      error: err => this.logger.error('Erro ao carregar países', err)
    });
  }

  get todosItens(): ZipContagemItem[] {
    return this.resultado?.zips ?? [];
  }

  get itens(): ZipContagemItem[] {
    const f = this.filtroLocal;
    return this.todosItens.filter(i =>
      (!f.provider || i.provider === f.provider) &&
      (!f.envio || i.envio === f.envio) &&
      (!f.pdv || i.nomePdv === f.pdv));
  }

  get opcoesProvider(): string[] {
    return this.distintos(i => i.provider);
  }

  get opcoesEnvio(): string[] {
    return this.distintos(i => i.envio);
  }

  get opcoesPdv(): string[] {
    return this.distintos(i => i.nomePdv);
  }

  get resumo() {
    const l = this.itens;
    const soma = (sel: (i: ZipContagemItem) => number) => l.reduce((t, i) => t + sel(i), 0);
    return {
      zips: l.length,
      imagens: soma(i => i.imagens),
      outros: soma(i => i.outrosArquivos),
      total: soma(i => i.totalArquivos),
      erros: l.filter(i => i.erro).length
    };
  }

  private distintos(sel: (i: ZipContagemItem) => string | null): string[] {
    return [...new Set(this.todosItens.map(sel).filter((v): v is string => !!v))].sort();
  }

  aplicarFiltroLocal(): void {
    this.currentPage = 1;
  }

  get podeConsultar(): boolean {
    return !!this.filtro.pais && !!this.filtro.mes;
  }

  private buildParams(): HttpParams {
    let p = new HttpParams()
      .set('pais', this.filtro.pais.toLowerCase())
      .set('mes', this.filtro.mes)
      .set('incluirSubpastas', String(this.filtro.incluirSubpastas));
    if (this.filtro.ano) p = p.set('ano', this.filtro.ano);
    return p;
  }

  private mensagemErro(err: any): string {
    return typeof err?.error === 'string' && err.error ? err.error : 'Erro ao consultar zips.';
  }

  consultar(): void {
    if (!this.podeConsultar) return;
    this.erro = '';
    this.currentPage = 1;
    this.filtroLocal = { provider: '', envio: '', pdv: '' };
    this.loadingService.show();
    this.jsonService.getZipsContagem(this.buildParams()).subscribe({
      next: resp => {
        this.resultado = resp;
        this.loadingService.hide();
      },
      error: err => {
        this.resultado = null;
        this.erro = this.mensagemErro(err);
        this.logger.error('Erro ao consultar zips-contagem', err);
        this.loadingService.hide();
      }
    });
  }

  limparFiltro(): void {
    this.filtro.pais = '';
    this.filtro.ano = String(new Date().getFullYear());
    this.filtro.mes = this.meses[new Date().getMonth()];
    this.filtroLocal = { provider: '', envio: '', pdv: '' };
    this.resultado = null;
    this.erro = '';
    this.currentPage = 1;
  }

  exportarCsv(): void {
    if (!this.resultado) return;
    const csv = (v: string | null | undefined) => (v ?? '').replace(/;/g, ',').replace(/[\r\n]/g, ' ');
    const fmt = (d: string) => {
      const dt = new Date(d);
      const p = (n: number) => String(n).padStart(2, '0');
      return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}:${p(dt.getSeconds())}`;
    };
    const comErro = this.itens.some(i => i.erro);
    const linhas = [
      'DataHora;Path;Arquivo;Pais;Mes;Provedor;Envio;Cod.Cidade;Cod.Farmacia;Nome;Tamanho(Mb);Qtde.Imagens;Qtde.Outros;Total' + (comErro ? ';Erro' : '')
    ];
    for (const z of this.itens) {
      linhas.push([
        fmt(z.dataHora), csv(z.caminhoCompleto), csv(z.zipName), csv(z.pais), csv(z.mes),
        csv(z.provider), csv(z.envio), csv(z.codigoCidade), csv(z.codigoFarmacia), csv(z.nomePdv),
        z.tamanhoMb.toFixed(2), z.imagens, z.outrosArquivos, z.totalArquivos
      ].join(';') + (comErro ? ';' + csv(z.erro) : ''));
    }
    const r = this.resumo;
    linhas.push(['TOTAL', '', '', '', '', '', '', '', '', '', '', r.imagens, r.outros, r.total].join(';') + (comErro ? ';' + r.erros : ''));

    const blob = new Blob(['\uFEFF' + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `zips_contagem_${this.filtro.pais}_${this.filtro.mes}_${this.filtro.ano}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
}
